/**
 * Small inline icon set for the app shell. Kept intentionally minimal (no
 * icon library dependency, matching the "no unnecessary dependency"
 * constraint) — a handful of 18x18 stroke icons is all the shell needs.
 */
import type { SVGProps } from 'react';
import logoMark from '../assets/brand/logo-mark.svg';
import logoMarkSmall from '../assets/brand/logo-mark-small.svg';

function Svg(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    />
  );
}

export function DashboardIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <rect x="2.5" y="2.5" width="6" height="6" rx="1" />
      <rect x="9.5" y="2.5" width="6" height="4" rx="1" />
      <rect x="9.5" y="8.5" width="6" height="7" rx="1" />
      <rect x="2.5" y="10.5" width="6" height="5" rx="1" />
    </Svg>
  );
}

export function TicketsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M2.5 6.5A1.5 1.5 0 0 1 4 5h10a1.5 1.5 0 0 1 1.5 1.5v1a1.5 1.5 0 0 0 0 3v1A1.5 1.5 0 0 1 14 13H4a1.5 1.5 0 0 1-1.5-1.5v-1a1.5 1.5 0 0 0 0-3z" />
    </Svg>
  );
}

export function UserIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="6" r="2.75" />
      <path d="M3.5 15c.7-2.8 3-4.5 5.5-4.5s4.8 1.7 5.5 4.5" />
    </Svg>
  );
}

export function UnassignedIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="9" r="6.5" strokeDasharray="2.4 2.4" />
    </Svg>
  );
}

export function ConfirmIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="9" r="6.5" />
      <path d="M9 5.5v4l2.6 1.6" />
    </Svg>
  );
}

export function ResolvedIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="9" r="6.5" />
      <path d="M6 9.2l2.1 2.1 4.1-4.6" />
    </Svg>
  );
}

export function ClosedIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <rect x="3" y="3" width="12" height="12" rx="2.5" />
      <path d="M6.5 6.5l5 5m0-5l-5 5" />
    </Svg>
  );
}

export function MenuIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M2.5 5h13M2.5 9h13M2.5 13h13" />
    </Svg>
  );
}

export function ChevronLeftIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M11 3.5L5.5 9l5.5 5.5" />
    </Svg>
  );
}

export function SearchIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="8" cy="8" r="5" />
      <path d="M15.5 15.5L12 12" />
    </Svg>
  );
}

export function SunIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="9" r="3.25" />
      <path d="M9 1.8v1.6M9 14.6v1.6M2.9 9H1.3M16.7 9h-1.6M4.3 4.3l1.1 1.1M12.6 12.6l1.1 1.1M13.7 4.3l-1.1 1.1M5.4 12.6l-1.1 1.1" />
    </Svg>
  );
}

export function MoonIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M14.8 10.8A6 6 0 0 1 7.2 3.2a6.3 6.3 0 1 0 7.6 7.6z" />
    </Svg>
  );
}

export function SoundOnIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M3 7.2h2.6L9 4.3v9.4L5.6 10.8H3z" />
      <path d="M11.6 6.6a3.3 3.3 0 0 1 0 4.8M13.4 4.8a5.8 5.8 0 0 1 0 8.4" />
    </Svg>
  );
}

export function SoundOffIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M3 7.2h2.6L9 4.3v9.4L5.6 10.8H3z" />
      <path d="M11.8 7l3.4 4M15.2 7l-3.4 4" />
    </Svg>
  );
}

export function LogoutIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M7 15.5H4a1.5 1.5 0 0 1-1.5-1.5V4A1.5 1.5 0 0 1 4 2.5h3" />
      <path d="M12 12.5L15.5 9 12 5.5" />
      <path d="M15.5 9H6.5" />
    </Svg>
  );
}

export function PlusIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M9 3.5v11M3.5 9h11" />
    </Svg>
  );
}

export function ChevronDownIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M4.5 7l4.5 4.5L13.5 7" />
    </Svg>
  );
}

export function ChevronRightIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M7 4.5L11.5 9 7 13.5" />
    </Svg>
  );
}

export function CloseIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M4.5 4.5l9 9m0-9l-9 9" />
    </Svg>
  );
}

export function TeamsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="6" r="2.1" />
      <circle cx="4.2" cy="7.4" r="1.55" />
      <circle cx="13.8" cy="7.4" r="1.55" />
      <path d="M5.4 14.5c.4-2 1.8-3.4 3.6-3.4s3.2 1.4 3.6 3.4M1.8 13.6c.3-1.5 1.2-2.5 2.5-2.6M16.2 13.6c-.3-1.5-1.2-2.5-2.5-2.6" />
    </Svg>
  );
}

export function DirectoryIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="6.75" cy="6.25" r="2.5" />
      <path d="M2.25 14.75c.5-2.5 2.3-4 4.5-4s4 1.5 4.5 4M12.5 5h3.25M12.5 8h3.25M13.5 11h2.25" />
    </Svg>
  );
}

export function CatalogueIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <rect x="2.75" y="2.75" width="12.5" height="12.5" rx="2" />
      <path d="M9 2.75v12.5M2.75 9h12.5M4.9 5.9h2M11 5.9h2M12 4.9v2M4.9 12.1h2M11 12.1h2" />
    </Svg>
  );
}

export function SlaIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="10" r="5.75" />
      <path d="M9 7v3.2l2 1.3M7.2 2.5h3.6" />
    </Svg>
  );
}

export function RoutingIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="9" r="6.5" />
      <path d="M11.6 6.4l-1.3 3.9-3.9 1.3 1.3-3.9z" />
    </Svg>
  );
}

export function SettingsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="9" r="2.25" />
      <path d="M9 1.9l1.3 1.9 2.2-.6.4 2.3 2.1 1-1 2.1 1 2.1-2.1 1-.4 2.3-2.2-.6L9 16.1l-1.3-1.9-2.2.6-.4-2.3-2.1-1 1-2.1-1-2.1 2.1-1 .4-2.3 2.2.6z" />
    </Svg>
  );
}

export function AuditIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M5.5 2.5h5l3.25 3.25V14a1.5 1.5 0 0 1-1.5 1.5H5.5A1.5 1.5 0 0 1 4 14V4a1.5 1.5 0 0 1 1.5-1.5z" />
      <path d="M10.5 2.5v3.25h3.25M6.75 9.25l1.5 1.5 3-3" />
    </Svg>
  );
}

export function AdminIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M9 2l5.5 2v4.3c0 3.4-2.3 6.3-5.5 7.7-3.2-1.4-5.5-4.3-5.5-7.7V4z" />
      <path d="M6.6 9l1.7 1.7 3.2-3.4" />
    </Svg>
  );
}

/** Edit (a pencil). */
export function EditIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M11.9 3.6l2.5 2.5-7.8 7.8-3.1.6.6-3.1z" />
      <path d="M10.4 5.1l2.5 2.5" />
    </Svg>
  );
}

/** Deactivate (a circle with a slash). */
export function DeactivateIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="9" r="6.5" />
      <path d="M4.4 4.4l9.2 9.2" />
    </Svg>
  );
}

export function KeyboardReturnIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M14.5 4.5v4a2 2 0 0 1-2 2h-8M7 8L4.5 10.5 7 13" />
    </Svg>
  );
}

export function MailIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <rect x="2.5" y="4" width="13" height="10" rx="1.5" />
      <path d="M3 5l6 4.5L15 5" />
    </Svg>
  );
}

export function GlobeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="9" r="6.5" />
      <path d="M2.5 9h13M9 2.5c1.8 1.8 2.6 4 2.6 6.5S10.8 13.7 9 15.5C7.2 13.7 6.4 11.5 6.4 9S7.2 4.3 9 2.5z" />
    </Svg>
  );
}

/**
 * The product mark: the CloudNine logo (src/assets/brand: logo-mark.svg is the full lockup, logo-mark-small.svg the icon only). The single
 * source for every in-app placement — sidebar, confirm page, sign-in screen —
 * so the logo can be changed in one place. Decorative: the brand name always
 * sits next to it (or in the link's aria-label), so alt is empty.
 *
 * Size it with CSS `height`; `width: auto` keeps the aspect ratio.
 */
export function BrandMark({ className, large = false }: { className?: string | undefined; large?: boolean }) {
  return (
    <img
      className={className}
      src={large ? logoMark : logoMarkSmall}
      alt=""
      width={large ? 70 : 36}
      height={large ? 65 : 45}
      decoding="async"
      draggable={false}
    />
  );
}

// ---- Dashboard glyphs (same 18px / 1.5 stroke grid as the shell icons) ----

export function NewTicketIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M5 2.5h5.5l3 3v10H5z" />
      <path d="M10.5 2.5v3h3M9.25 8.5v4M7.25 10.5h4" />
    </Svg>
  );
}

export function OpenTicketsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="7" cy="6" r="2.5" />
      <path d="M2.5 14.5c.5-2.4 2.3-3.9 4.5-3.9 1 0 1.9.3 2.6.8" />
      <circle cx="13" cy="12.5" r="2.5" />
    </Svg>
  );
}

export function InProgressIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M15.5 9A6.5 6.5 0 1 1 9 2.5" />
      <path d="M12.8 3.6l.1.1M14.6 5.8l.1.1" />
    </Svg>
  );
}

export function WaitingIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="9" r="6.5" />
      <path d="M9 5.25V9l2.5 1.5" />
    </Svg>
  );
}

export function ArrowUpIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M9 14V4M5 8l4-4 4 4" />
    </Svg>
  );
}

export function ArrowDownIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M9 4v10M5 10l4 4 4-4" />
    </Svg>
  );
}

export function BoltIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M10 1.75L3.75 10H9l-1 6.25L14.25 8H9z" />
    </Svg>
  );
}

export function PulseIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M1.75 9h3l2-4.5 3.5 9 2-4.5h4" />
    </Svg>
  );
}

export function CommentIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M3 4.5A1.5 1.5 0 0 1 4.5 3h9A1.5 1.5 0 0 1 15 4.5v6a1.5 1.5 0 0 1-1.5 1.5H8l-3.5 3v-3A1.5 1.5 0 0 1 3 10.5z" />
    </Svg>
  );
}

export function ReopenIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M3 9a6 6 0 1 0 1.8-4.3M3 2.75V5h2.25" />
    </Svg>
  );
}

export function AttachmentIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M14.5 8.25l-5.6 5.6a3.25 3.25 0 0 1-4.6-4.6l6-6a2.2 2.2 0 0 1 3.1 3.1l-6 6a1.1 1.1 0 0 1-1.6-1.6l5.4-5.4" />
    </Svg>
  );
}

export function WarningIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M9 2.75l6.75 12H2.25z" />
      <path d="M9 7.25v3.25M9 12.75v.01" />
    </Svg>
  );
}

// ---- Sidebar navigation glyphs (20px grid, 1.6 stroke — the reference nav size) ----

function Svg20(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    />
  );
}

/** Home. Fills with `currentColor` when an ancestor sets `--icon-fill` (the active nav row does). */
export function HomeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <path
        d="M3.5 9.1L10 3.4l6.5 5.7v6.9a1 1 0 0 1-1 1h-3.25v-4.5h-4.5V17H4.5a1 1 0 0 1-1-1z"
        style={{ fill: 'var(--icon-fill, none)' }}
      />
    </Svg20>
  );
}

export function AllTicketsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <rect x="2.75" y="3.75" width="14.5" height="12.5" rx="2" />
      <path d="M2.75 7.5h14.5M5.5 5.6h.01M7.5 5.6h.01" />
    </Svg20>
  );
}

export function UnassignedUserIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <circle cx="8" cy="6.75" r="2.75" />
      <path d="M2.75 16.25c.6-3 2.7-4.75 5.25-4.75.9 0 1.7.2 2.45.6" />
      <path d="M13.4 10.1a1.75 1.75 0 1 1 2.5 1.6c-.5.25-.75.6-.75 1.05v.35M15.15 15.4v.01" />
    </Svg20>
  );
}

export function AwaitingIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <rect x="3.25" y="3.25" width="13.5" height="13.5" rx="2.75" />
      <path d="M6.9 10.2l2.1 2.1 4.1-4.5" />
    </Svg20>
  );
}

export function ResolvedNavIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <path d="M5.75 3.25h8.5a2.5 2.5 0 0 1 2.5 2.5v6.75a2.5 2.5 0 0 1-2.5 2.5h-2.4L10 16.9 8.15 15H5.75a2.5 2.5 0 0 1-2.5-2.5V5.75a2.5 2.5 0 0 1 2.5-2.5z" />
      <path d="M7 9.1l2.05 2.05 4-4.3" />
    </Svg20>
  );
}

export function ClosedNavIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <path d="M10 2.75l5.75 2.2v4.55c0 3.6-2.4 6.35-5.75 7.75-3.35-1.4-5.75-4.15-5.75-7.75V4.95z" />
      <path d="M7.35 10l1.8 1.8 3.5-3.7" />
    </Svg20>
  );
}

export function BellIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <path d="M5 8.6a5 5 0 0 1 10 0c0 3.9 1.6 5.4 1.6 5.4H3.4S5 12.5 5 8.6z" />
      <path d="M8.2 16.6a1.9 1.9 0 0 0 3.6 0" />
    </Svg20>
  );
}

/** Incident Reports: a report sheet with an alert mark. */
export function IncidentReportIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <path d="M5.5 2.8h6.2l3.3 3.3v10.1a1 1 0 0 1-1 1H5.5a1 1 0 0 1-1-1V3.8a1 1 0 0 1 1-1z" />
      <path d="M11.5 2.8v3.5h3.5" />
      <path d="M9.8 8.7v3.1" />
      <path d="M9.8 14h.01" />
    </Svg20>
  );
}

/** IT Invoice Submission: a receipt with a torn (zig-zag) foot and line items. */
export function InvoiceIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <path d="M5 2.8h10a.5.5 0 0 1 .5.5v13.9l-1.9-1.2-1.9 1.2-1.7-1.2-1.7 1.2-1.9-1.2-1.9 1.2V3.3a.5.5 0 0 1 .5-.5z" />
      <path d="M7.5 6.5h5" />
      <path d="M7.5 9.3h5" />
      <path d="M7.5 12.1h2.8" />
    </Svg20>
  );
}

/** Design Workflow: a pencil drawing a line on a page. */
export function DesignWorkflowIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <path d="M3.5 16.5l.9-3.9 8.4-8.4a1.5 1.5 0 0 1 2.1 0l.8.8a1.5 1.5 0 0 1 0 2.1l-8.4 8.4z" />
      <path d="M11.4 5.6l3 3" />
      <path d="M10 16.5h6.5" />
    </Svg20>
  );
}

/** IT Checklist: a clipboard with ticked lines. */
export function ItChecklistIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <path d="M6.5 4.3H5a1 1 0 0 0-1 1v10.9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V5.3a1 1 0 0 0-1-1h-1.5" />
      <rect x="6.5" y="2.8" width="7" height="3" rx="1" />
      <path d="M7 9.8l1.2 1.2 2-2.2" />
      <path d="M11.8 10h2.2" />
      <path d="M7 14l1.2 1.2 2-2.2" />
      <path d="M11.8 14.2H14" />
    </Svg20>
  );
}

/** IT Checklist Monitor: a clipboard with a live pulse line. */
export function ItChecklistMonitorIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <path d="M6.5 4.3H5a1 1 0 0 0-1 1v10.9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V5.3a1 1 0 0 0-1-1h-1.5" />
      <rect x="6.5" y="2.8" width="7" height="3" rx="1" />
      <path d="M6.2 12h1.9l1.2-2.6 1.6 4.6 1.2-2h1.7" />
    </Svg20>
  );
}

export function AdminGroupIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <circle cx="8" cy="6.5" r="2.75" />
      <path d="M2.75 16.25c.6-3 2.7-4.75 5.25-4.75.6 0 1.2.1 1.75.3" />
      <circle cx="14.5" cy="14.25" r="1.6" />
      <path d="M14.5 11.25v.8M14.5 16.45v.8M11.5 14.25h.8M16.7 14.25h.8M12.4 12.15l.55.55M16.05 15.8l.55.55M16.6 12.15l-.55.55M12.95 15.8l-.55.55" />
    </Svg20>
  );
}

/** Collapse/expand the sidebar column (a panel with its left rail). */
export function SidebarToggleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg20 {...props}>
      <rect x="2.75" y="3.75" width="14.5" height="12.5" rx="2" />
      <path d="M7.75 3.75v12.5M4.75 7h1M4.75 9.5h1" />
    </Svg20>
  );
}

/** Two-tone filled cloud for the hosting label in the sidebar footer. Decorative. */
/** Outline cloud on the shared 18px / 1.5 stroke grid (the login screen's feature list). */
export function CloudOutlineIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M5.2 14.25a3.45 3.45 0 0 1-.35-6.88 4.6 4.6 0 0 1 8.85-.62 3.75 3.75 0 0 1-.2 7.5z" />
    </Svg>
  );
}

/**
 * Microsoft's four-square mark for the "Sign in with Microsoft" button only
 * (Microsoft identity platform branding guidelines). It identifies the
 * sign-in provider — it is never the product's own logo.
 */
export function MicrosoftLogo(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" focusable="false" {...props}>
      <rect x="1" y="1" width="9" height="9" fill="currentColor" />
      <rect x="11" y="1" width="9" height="9" fill="currentColor" />
      <rect x="1" y="11" width="9" height="9" fill="currentColor" />
      <rect x="11" y="11" width="9" height="9" fill="currentColor" />
    </svg>
  );
}

export function CloudIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width="26" height="18" viewBox="0 0 26 18" aria-hidden="true" {...props}>
      <path d="M7 16.5a5 5 0 0 1-.6-9.96A7 7 0 0 1 19.3 5.2 5.7 5.7 0 0 1 19.5 16.5z" fill="var(--accent-300)" />
      <path d="M11 16.5a4.5 4.5 0 0 1-.4-8.98A6 6 0 0 1 21.9 8.9 3.9 3.9 0 0 1 21 16.5z" fill="var(--color-accent)" />
    </svg>
  );
}
